import { useEffect, useMemo, useRef, useState } from "react";
import { CSS_CLASSES } from "../constants/app";
import {
  buildAnswersFromSubmission,
  formatTimelineDateTime,
  isAnswered,
  shouldShowQuestion
} from "./questionnaire/questionnaire.utils";
import type {
  FlatQuestionItem,
  QuestionnaireHistoryItem,
  QuestionnaireMode,
  QuestionnaireTabProps
} from "./questionnaire/questionnaire.types";
import {
  QuestionnaireFormSkeleton
} from "./questionnaire/QuestionnaireSkeletons";
import QuestionnaireHistoryView from "./questionnaire/QuestionnaireHistoryView";
import QuestionnaireFormView from "./questionnaire/QuestionnaireFormView";
import { useQuestionnaireApi } from "./questionnaire/useQuestionnaireApi";
import { useQuestionnaireFlow } from "./questionnaire/useQuestionnaireFlow";
import type {
  QuestionnaireAnswersMap,
} from "../types";

const TRANSITION_MS = 420;
const QUESTIONNAIRE_FORM_GROUP = "heart_health";
const QUESTIONNAIRE_VERSION = "v1";

export default function QuestionnaireTab({ patient: _patient, onQuestionnaireSubmitted }: QuestionnaireTabProps) {
  const patient = _patient;
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [optionalGroupExpanded, setOptionalGroupExpanded] = useState(false);
  const [mode, setMode] = useState<QuestionnaireMode>("history");
  const [selectedSubmission, setSelectedSubmission] = useState<QuestionnaireHistoryItem | null>(null);
  const [answers, setAnswers] = useState<QuestionnaireAnswersMap>({});
  const [answersByQuestionnaireId, setAnswersByQuestionnaireId] = useState<
    Record<string, QuestionnaireAnswersMap>
  >({});
  const [currentVisibleIndex, setCurrentVisibleIndex] = useState(0);
  const lastHydratedQuestionnaireRef = useRef<string | null>(null);

  const {
    sections,
    sectionsLoading,
    sectionsError,
    history,
    historyLoading,
    historyError,
    fetchQuestionnaireHistory
  } = useQuestionnaireApi({
    patientUserId: patient?.userId,
    formGroup: QUESTIONNAIRE_FORM_GROUP
  });

  const normalizedGender = (patient?.personal_information?.gender || "").trim().toLowerCase();
  const isFemalePatient = normalizedGender === "female";
  const visibleSections = useMemo(
    () =>
      sections.filter(({ section }) => {
        if (section.sectionId !== "female_specific") return true;
        return isFemalePatient;
      }),
    [sections, isFemalePatient]
  );

  useEffect(() => {
    setCurrentSectionIndex((prev) => {
      if (visibleSections.length === 0) return 0;
      return Math.min(prev, visibleSections.length - 1);
    });
  }, [visibleSections]);

  const currentSectionEntry = visibleSections[currentSectionIndex] || null;
  const allQuestions = useMemo<FlatQuestionItem[]>(
    () =>
      currentSectionEntry
        ? currentSectionEntry.section.questions.map((question) => ({
            section: currentSectionEntry.section,
            question
          }))
        : [],
    [currentSectionEntry]
  );

  const questionLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    visibleSections.forEach(({ section }) => {
      section.questions.forEach((question) => {
        map.set(question.questionId, question.label);
      });
    });
    return map;
  }, [visibleSections]);

  const questionnaireSectionTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    visibleSections.forEach(({ questionnaireId, section }) => {
      map.set(questionnaireId, section.title);
    });
    return map;
  }, [visibleSections]);

  const questionOptionLabelMap = useMemo(() => {
    const map = new Map<string, Map<string, string>>();
    visibleSections.forEach(({ section }) => {
      section.questions.forEach((question) => {
        const optionMap = new Map<string, string>();
        (question.options || []).forEach((option) => {
          optionMap.set(option.value, option.label);
        });
        map.set(question.questionId, optionMap);
      });
    });
    return map;
  }, [visibleSections]);

  const questionUnitMap = useMemo(() => {
    const map = new Map<string, string>();
    visibleSections.forEach(({ section }) => {
      section.questions.forEach((question) => {
        if (question.unit) {
          map.set(question.questionId, question.unit);
        }
      });
    });
    return map;
  }, [visibleSections]);

  const latestSubmissionByQuestionnaireId = useMemo(() => {
    const map = new Map<string, QuestionnaireHistoryItem>();
    history.forEach((entry) => {
      if (!map.has(entry.questionnaireId)) {
        map.set(entry.questionnaireId, entry);
      }
    });
    return map;
  }, [history]);

  const toDisplayLabel = (questionId: string, rawValue: string) => {
    const optionMap = questionOptionLabelMap.get(questionId);
    return optionMap?.get(rawValue) || rawValue;
  };

  const appendUnitWhenApplicable = (questionId: string, rawValue: string, resolvedValue: string) => {
    const unit = questionUnitMap.get(questionId);
    if (!unit) return resolvedValue;

    // If the value got converted to an option label (e.g., yes/no), don't append unit.
    if (resolvedValue !== rawValue) return resolvedValue;

    const trimmed = resolvedValue.trim();
    if (!trimmed) return resolvedValue;
    return `${trimmed} ${unit}`;
  };

  const formatAnswerValue = (
    questionId: string,
    answer: {
    value?: string;
    data?: Array<{ value: string; textInput?: string }>;
  }
  ) => {
    if (Array.isArray(answer.data) && answer.data.length > 0) {
      return answer.data
        .map((item) => {
          const rawValue = typeof item.textInput === "string" && item.textInput.trim()
            ? item.textInput.trim()
            : item.value;
          const resolvedValue = toDisplayLabel(questionId, rawValue);
          return appendUnitWhenApplicable(questionId, rawValue, resolvedValue);
        })
        .filter(Boolean)
        .join(", ");
    }
    if (!answer.value) return "-";
    const resolvedValue = toDisplayLabel(questionId, answer.value);
    return appendUnitWhenApplicable(questionId, answer.value, resolvedValue);
  };

  const visibleQuestions = useMemo(
    () =>
      allQuestions.filter(({ question }) => shouldShowQuestion(question, answers)),
    [allQuestions, answers]
  );

  useEffect(() => {
    setCurrentVisibleIndex((prev) =>
      Math.min(Math.max(prev, 0), Math.max(visibleQuestions.length - 1, 0))
    );
  }, [visibleQuestions.length]);

  useEffect(() => {
    setAnswers((previousAnswers) => {
      const visibleIds = new Set(visibleQuestions.map((item) => item.question.questionId));
      let hasChange = false;
      const nextAnswers: QuestionnaireAnswersMap = {};

      Object.entries(previousAnswers).forEach(([questionId, value]) => {
        if (visibleIds.has(questionId)) {
          nextAnswers[questionId] = value;
        } else {
          hasChange = true;
        }
      });

      if (hasChange && currentSectionEntry?.questionnaireId) {
        setAnswersByQuestionnaireId((prev) => ({
          ...prev,
          [currentSectionEntry.questionnaireId]: nextAnswers
        }));
      }

      return hasChange ? nextAnswers : previousAnswers;
    });
  }, [visibleQuestions, currentSectionEntry?.questionnaireId]);

  const currentItem = visibleQuestions[currentVisibleIndex];

  useEffect(() => {
    if (mode !== "form" || !currentSectionEntry?.questionnaireId) return;
    const questionnaireId = currentSectionEntry.questionnaireId;
    if (lastHydratedQuestionnaireRef.current === questionnaireId) return;
    const existingDraft = answersByQuestionnaireId[questionnaireId];

    if (!existingDraft) {
      const latest = latestSubmissionByQuestionnaireId.get(questionnaireId);
      const prefilledAnswers = buildAnswersFromSubmission(latest);
      setAnswers(prefilledAnswers);
      setAnswersByQuestionnaireId((prev) => ({ ...prev, [questionnaireId]: prefilledAnswers }));
      setCurrentVisibleIndex(0);
    } else if (answers !== existingDraft) {
      setAnswers(existingDraft);
      setCurrentVisibleIndex(0);
    }

    lastHydratedQuestionnaireRef.current = questionnaireId;
  }, [
    mode,
    currentSectionEntry?.questionnaireId,
    latestSubmissionByQuestionnaireId,
    answersByQuestionnaireId,
    answers
  ]);

  const {
    submitLoading,
    sectionSwitchLoading,
    submitError,
    direction,
    outgoingItem,
    isTransitioning,
    enterActive,
    validationError,
    setValidationError,
    setSubmitError,
    clearTransitions,
    updateAnswer,
    clearSingleSelectAnswer,
    toggleMultiOption,
    updateOptionText,
    getOptionText,
    startNewQuestionnaire,
    goNext,
    goBack
  } = useQuestionnaireFlow({
    patientUserId: patient?.userId,
    sections: visibleSections,
    currentSectionIndex,
    setCurrentSectionIndex,
    currentSectionEntry,
    currentItem,
    currentVisibleIndex,
    setCurrentVisibleIndex,
    visibleQuestions,
    answers,
    setAnswers,
    answersByQuestionnaireId,
    setAnswersByQuestionnaireId,
    latestSubmissionByQuestionnaireId,
    fetchQuestionnaireHistory,
    questionnaireVersion: QUESTIONNAIRE_VERSION,
    transitionMs: TRANSITION_MS,
    onQuestionnaireSubmitted
  });

  const sectionStats = useMemo(() => {
    return visibleSections.map(({ questionnaireId, section, isOptionalSection, sectionGroupLabel }) => {
      const isCurrentSection =
        questionnaireId === currentSectionEntry?.questionnaireId;
      const sectionAnswers = isCurrentSection
        ? answers
        : answersByQuestionnaireId[questionnaireId] || {};
      const items = section.questions
        .filter((item) => shouldShowQuestion(item, sectionAnswers))
        .map((question) => ({ section, question }));
      const answeredAny = items.filter((item) =>
        isAnswered(item.question, sectionAnswers[item.question.questionId])
      ).length;

      const allVisibleDone = items.length > 0 && answeredAny === items.length;

      let status: "pending" | "in_progress" | "completed" = "pending";
      if (allVisibleDone) {
        status = "completed";
      } else if (answeredAny > 0) {
        status = "in_progress";
      }

      return {
        sectionId: section.sectionId,
        questionnaireId,
        title: section.title,
        total: items.length,
        answered: answeredAny,
        status,
        isOptionalSection: Boolean(isOptionalSection),
        sectionGroupLabel
      };
    });
  }, [answers, answersByQuestionnaireId, visibleSections, currentSectionEntry?.questionnaireId]);

  if (mode === "history") {
    return (
      <div className="flex h-full min-h-0 flex-col">
      <QuestionnaireHistoryView
        history={history}
        historyLoading={historyLoading}
        historyError={historyError}
        selectedSubmission={selectedSubmission}
        setSelectedSubmission={setSelectedSubmission}
        onAddQuestionnaire={() => {
          startNewQuestionnaire();
          lastHydratedQuestionnaireRef.current = null;
          setMode("form");
        }}
        addButtonClassName={CSS_CLASSES.BUTTONS.PRIMARY_PATIENT}
        questionLabelMap={questionLabelMap}
        questionnaireSectionTitleMap={questionnaireSectionTitleMap}
        formatAnswerValue={formatAnswerValue}
        formatTimelineDateTime={formatTimelineDateTime}
      />
      </div>
    );
  }

  if (sectionsLoading) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <QuestionnaireFormSkeleton />
      </div>
    );
  }
  if (sectionsError) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className={`py-6 ${CSS_CLASSES.TEXT.ERROR}`}>{sectionsError}</div>
      </div>
    );
  }
  if (!currentItem) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="py-6 text-sm text-guardian-header">No questionnaire sections available.</div>
      </div>
    );
  }

  const { section, question } = currentItem;
  const currentAnswer = answers[question.questionId] ?? null;
  const outgoingAnswer = outgoingItem
    ? answers[outgoingItem.question.questionId] ?? null
    : null;
  const currentSectionQuestions = visibleQuestions.filter(
    (item) => item.section.sectionId === section.sectionId
  );
  const currentQuestionIndexInSection = currentSectionQuestions.findIndex(
    (item) => item.question.questionId === question.questionId
  );
  const sectionProgress =
    currentSectionQuestions.length > 0
      ? ((currentQuestionIndexInSection + 1) / currentSectionQuestions.length) * 100
      : 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
    <QuestionnaireFormView
      sectionTitle="Heart Health"
      sectionStats={sectionStats}
      activeQuestionnaireId={currentSectionEntry?.questionnaireId || ""}
      optionalGroupExpanded={optionalGroupExpanded}
      optionalGroupLabel={
        sectionStats.find((item) => item.isOptionalSection)?.sectionGroupLabel ||
        "More questionnaires (Optional)"
      }
      onToggleOptionalGroup={() => setOptionalGroupExpanded((prev) => !prev)}
      onSectionSelect={(questionnaireId) => {
        const sectionIdx = visibleSections.findIndex((entry) => entry.questionnaireId === questionnaireId);
        if (sectionIdx < 0) return;
        clearTransitions();
        setCurrentSectionIndex(sectionIdx);
        setCurrentVisibleIndex(0);
        const targetQuestionnaireId = visibleSections[sectionIdx]?.questionnaireId;
        setAnswers(
          (targetQuestionnaireId && answersByQuestionnaireId[targetQuestionnaireId]) || {}
        );
        setValidationError("");
        setSubmitError(null);
      }}
      currentVisibleIndex={currentVisibleIndex}
      onBack={goBack}
      onBackToHistory={() => {
        setMode("history");
        setValidationError("");
        setSubmitError(null);
      }}
      sectionSwitchLoading={sectionSwitchLoading}
      outgoingItem={outgoingItem}
      isTransitioning={isTransitioning}
      enterActive={enterActive}
      direction={direction}
      outgoingAnswer={outgoingAnswer}
      section={section}
      question={question}
      currentAnswer={currentAnswer}
      updateAnswer={(value) => updateAnswer(question.questionId, value)}
      onClearSingleSelect={() => clearSingleSelectAnswer(question.questionId)}
      toggleMultiOption={(value) =>
        toggleMultiOption(question.questionId, currentAnswer, value, question.options || [])
      }
      updateOptionText={(optionValue, textValue) =>
        updateOptionText(question.questionId, optionValue, textValue)
      }
      getOptionText={(optionValue) => getOptionText(question.questionId, optionValue)}
      validationError={validationError}
      submitError={submitError}
      currentQuestionIndexInSection={currentQuestionIndexInSection}
      currentSectionQuestionsLength={currentSectionQuestions.length}
      sectionProgress={sectionProgress}
      onNext={() =>
        goNext(() => {
          setMode("history");
        })
      }
      submitLoading={submitLoading}
      isLastVisibleQuestion={currentVisibleIndex === visibleQuestions.length - 1}
      hasMoreSections={currentSectionIndex < visibleSections.length - 1}
    />
    </div>
  );
}

